import {useInfiniteQuery} from 'react-query';
import {TableProps} from 'shared/migration-components/api-table';
import {PaginationDataProps} from 'shared/migration-components/api-table/state';
import useApi from 'shared/hooks/use-api';
import {AnalyticsCardProps} from 'shared/migration-components/analytics-card';
import {NestedKeys} from 'shared/util/nested-keys';
import {FilterEnum} from 'shared/components/date-filter';
import filterEnumToDateFilter from 'shared/util/filter-enum-to-date-filter';

export interface AnalyticsCardListProps
  extends Omit<AnalyticsCardProps, 'value'> {
  field: string | ((data: any) => string | number);
}

const useListState = (
  table: Omit<TableProps<any>, 'inifityQuery'>,
  queryKey: any = [table.url],
  extraParams: any = {},
  startDateFilter?: string,
  fixedColumns: NestedKeys[] = [],
) => {
  const api = useApi();
  const filters = table.projection.filter?.filters ?? {};
  const dateFilter = filters[startDateFilter ?? ''] as unknown as string;

  const onChangeDate = (
    filterType: FilterEnum,
    dateInterval?: {startDate: Date; endDate: Date},
  ) => {
    if (!table.projection.filter?.onChange || !startDateFilter) return;
    if (!filterType) {
      delete filters[startDateFilter];
      return table.projection.filter.onChange(filters);
    }

    table.projection.filter.onChange({
      ...filters,
      ...filterEnumToDateFilter({
        filter: filterType,
        dateInterval,
        startDateField: startDateFilter,
      }),
    });
  };

  const handleGetData = async (params: any): Promise<PaginationDataProps> => {
    const options = projection.options;
    const filters = Object.keys(projection.filter?.filters ?? {}).reduce(
      (filters, key) => {
        const filter = projection.filter!.filters[key];
        const option = options.find((option) =>
          typeof option === 'string' ? key === option : option.value === key,
        );

        if (typeof option === 'string') return {...filters, [key]: filter};

        const filterKey = option?.filterKey ?? (option?.value as string);

        return {
          ...filters,
          [filterKey]: Array.isArray(filter) ? JSON.stringify(filter) : filter,
        };
      },
      {},
    );

    const response = await api({
      method: 'GET',
      url,
      params: {
        projection: [
          ...(projection?.values ?? []).filter(
            (value) => !fixedColumns.includes(value),
          ),
          ...fixedColumns,
        ].join(' '),
        ...filters,
        ...extraParams,
        page: params?.pageParam ?? 0,
        limit: 10,
        orderField: projection.orderBy?.field ?? 'id',
        orderBy: projection.orderBy?.order ?? '1',
      },
    });

    return response.data;
  };

  const {url, projection} = table;

  return {
    ...useInfiniteQuery(
      [
        ...queryKey,
        [...(projection.values ?? [])].sort(),
        {
          ...projection.filter?.filters,
          ...extraParams,
          ...(projection.orderBy ?? {}),
        },
      ],
      handleGetData,
      {
        staleTime: 45 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
        getNextPageParam: (lastPage) => {
          const {currentPage, isLastPage} = lastPage;
          if (isLastPage) return;

          return currentPage + 1;
        },
      },
    ),
    onChangeDate,
    defaultValues:
      typeof dateFilter === 'string' ? JSON.parse(dateFilter) : undefined,
  };
};

export default useListState;
